import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Download, Search } from 'lucide-react';
import { toast } from 'sonner';

const fmtC = (n) => {
  if (n == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const SOURCE_BADGE = {
  'Bank Statement': 'bg-violet-500/15 text-violet-700 border-violet-500/20',
  'R03 Reversal':   'bg-amber-500/15 text-amber-700 border-amber-500/20',
  'Manual Ledger':  'bg-slate-500/15 text-slate-700 border-slate-500/20',
};

const csvCell = (v) => {
  const s = (v ?? '').toString();
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const FundsPulledBreakdownModal = ({ open, onOpenChange, accountId, orgName }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    if (!open || !accountId) return;
    let cancelled = false;
    setLoading(true);
    setSearch('');
    setSourceFilter('all');
    api.get('/payments/v2/funds-pulled/breakdown', { params: { account_id: accountId } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) toast.error('Could not load Funds Pulled breakdown'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, accountId]);

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (sourceFilter !== 'all' && r.source !== sourceFilter) return false;
      if (!q) return true;
      return (
        (r.description || '').toLowerCase().includes(q) ||
        (r.batch_name || '').toLowerCase().includes(q) ||
        (r.payment_file_id || '').toLowerCase().includes(q) ||
        (r.transaction_reference || '').toLowerCase().includes(q) ||
        (r.date || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, sourceFilter]);

  const filteredTotal = useMemo(
    () => filtered.reduce((acc, r) => acc + (Number(r.amount) || 0), 0),
    [filtered],
  );

  const downloadCsv = () => {
    if (!filtered.length) return;
    const header = ['Date', 'Amount (USD)', 'Source', 'Source Tag', 'Description', 'Transaction Reference', 'Partner'];
    const lines = [header.map(csvCell).join(',')];
    filtered.forEach((r) => {
      lines.push([
        r.date, r.amount, r.source, r.source_tag, r.description,
        r.transaction_reference, r.partner,
      ].map(csvCell).join(','));
    });
    const stamp = new Date().toISOString().slice(0, 10);
    const safeOrg = (orgName || 'org').toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `funds_pulled_${safeOrg}_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-5xl max-h-[85vh] flex flex-col p-0"
        data-testid="funds-pulled-breakdown-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-base font-semibold">
            Funds Pulled — Breakdown
            {orgName && <span className="text-muted-foreground font-normal text-sm"> · {orgName}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Every fund-pull line behind the KPI: bank statement credits, R03 reversals, and manual ledger entries. Click Download CSV for the filtered view.
          </DialogDescription>
        </DialogHeader>

        {/* Totals strip */}
        {totals && (
          <div className="grid grid-cols-4 gap-3 px-6 py-3 border-b bg-muted/30 text-xs">
            <div>
              <p className="text-muted-foreground">Bank Net</p>
              <p className="font-semibold text-violet-700" data-testid="fp-total-bank-net">{fmtC(totals.bank_net)}</p>
              <p className="text-[10px] text-muted-foreground">Gross {fmtC(totals.bank_gross)} − Reversals {fmtC(totals.bank_reversals)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Manual Ledger</p>
              <p className="font-semibold text-slate-700" data-testid="fp-total-legacy">{fmtC(totals.legacy)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Combined Total</p>
              <p className="font-semibold text-emerald-700" data-testid="fp-total-combined">{fmtC(totals.total)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Rows</p>
              <p className="font-semibold" data-testid="fp-row-count">{totals.row_count}</p>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search description, batch, PF id, reference, date…"
              data-testid="fp-search-input"
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs focus:border-foreground/40 focus:outline-none focus:ring-1 focus:ring-foreground/20"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            data-testid="fp-source-filter"
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
          >
            <option value="all">All sources</option>
            <option value="Bank Statement">Bank Statement</option>
            <option value="R03 Reversal">R03 Reversal</option>
            <option value="Manual Ledger">Manual Ledger</option>
          </select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={downloadCsv}
            disabled={!filtered.length}
            data-testid="fp-download-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Download CSV ({filtered.length})
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-6 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/30 py-10 text-center text-xs text-muted-foreground">
              No fund-pull rows match the current filter.
            </div>
          ) : (
            <Table data-testid="fp-table">
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[110px]">Date</TableHead>
                  <TableHead className="w-[130px] text-right">Amount</TableHead>
                  <TableHead className="w-[140px]">Source</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[180px]">Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r, idx) => {
                  const badgeCls = SOURCE_BADGE[r.source] || 'bg-muted text-foreground border-border';
                  const negative = (r.amount || 0) < 0;
                  return (
                    <TableRow key={r.ref_id || idx} data-testid={`fp-row-${idx}`}>
                      <TableCell className="font-mono text-xs">{r.date || '\u2014'}</TableCell>
                      <TableCell className={`text-right font-mono text-xs ${negative ? 'text-amber-700' : 'text-foreground'}`}>
                        {fmtC(r.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${badgeCls} text-[10px] font-medium`}>
                          {r.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="line-clamp-2" title={r.description}>{r.description || '\u2014'}</div>
                        {r.source_tag && r.source !== 'Manual Ledger' && (
                          <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{r.source_tag}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{r.transaction_reference || '\u2014'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer total */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between border-t px-6 py-2 text-xs bg-muted/30">
            <span className="text-muted-foreground">Showing {filtered.length} of {totals?.row_count || 0} rows</span>
            <span className="font-mono">
              Filtered total: <span className="font-semibold">{fmtC(filteredTotal)}</span>
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FundsPulledBreakdownModal;
